import React, { useLayoutEffect, useRef, useState } from 'react'

import classNames from 'classnames'

import { BaseAttribute } from 'features/views/attributes/editable/BaseAttribute'
import { useAttributeContext } from 'features/views/attributes/editable/hooks/useAttributeContext'
import { InlineEditingAttributeContextProvider } from 'features/views/attributes/editable/InlineEditingAttributeContextProvider'
import { LongTextAttributeEditor } from 'features/views/attributes/editable/LongTextAttribute'
import { RichTextAttributeEditor } from 'features/views/attributes/editable/RichTextAttribute'
import { EditableAttribute, EditableAttributeProps } from 'features/views/attributes/editable/types'
import { LongTextAttributeDisplay } from 'features/views/attributes/LongTextAttributeDisplay'
import { LongTextValue } from 'features/views/attributes/LongTextValue'
import { RichTextAttributeDisplay } from 'features/views/attributes/RichTextAttributeDisplay'
import { TextAttributeDisplay } from 'features/views/attributes/TextAttributeDisplay'
import { RichTextValue } from 'features/views/attributes/types'

import { Box } from 'ui/components/Box'
import { Body } from 'ui/components/Text'
import { body } from 'ui/components/Text/Body.css'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { useDetailViewSubtitleAttributeState } from './hooks/useDetailViewSubtitleAttributeState'

import { DetailViewSubtitleAttributeStyles } from './DetailViewSubtitleAttribute.css'

type DetailViewSubtitleAttributeProps = Pick<
    React.ComponentProps<typeof Body>,
    'style' | 'className' | 'size' | 'mt'
> & {
    isLoading?: boolean
    maxLines?: number
}

export const DetailViewSubtitleAttribute: React.FC<DetailViewSubtitleAttributeProps> = React.memo(
    function DetailViewSubtitleAttribute(props) {
        const { field, overrideIsEditable } = useDetailViewSubtitleAttributeState()
        if (!field) return null

        return (
            <InlineEditingAttributeContextProvider
                field={field}
                overrideIsEditable={overrideIsEditable}
            >
                <DetailViewSubtitleAttributeInner field={field} {...props} />
            </InlineEditingAttributeContextProvider>
        )
    }
)

type MoreLinkProps = {
    size: React.ComponentProps<typeof Body>['size']
    showFullText: boolean
    onClick: React.MouseEventHandler<HTMLElement>
}

const MoreLink: React.FC<MoreLinkProps> = ({ size, showFullText, onClick }) => {
    return (
        <Body
            display="block"
            mt="3xs"
            size={size}
            color="text"
            weight="bold"
            role="button"
            onClick={onClick}
        >
            {showFullText ? 'Show less' : 'Show more'}
        </Body>
    )
}

type DetailViewSubtitleAttributeInnerProps = DetailViewSubtitleAttributeProps & {
    field: EditableAttribute
}

const DetailViewSubtitleAttributeInner: React.FC<DetailViewSubtitleAttributeInnerProps> = ({
    field,
    style,
    mt,
    maxLines,
    size,
    isLoading,
    className: providedClassName,
    ...props
}) => {
    const effectiveSize = useResponsiveValue(size)
    const weight = 'medium' as const

    const { value, isEditingValue, isEditable } = useAttributeContext<unknown>()

    const textValue =
        field.field.type === 'document'
            ? (value as RichTextValue)?.plainTextContent
            : (value as string)
    const className = classNames(
        DetailViewSubtitleAttributeStyles.styleFunction({}),
        body.styleFunction({ size: effectiveSize, weight }),
        providedClassName
    )

    const isEmpty = !isLoading && !textValue

    const wrapperRef = useRef<HTMLDivElement>(null)

    const [showFullText, setShowFullText] = useState(false)

    const editorMaxHeight = useRef(0)
    useLayoutEffect(() => {
        const calculateHeight = requestAnimationFrame(() => {
            if (wrapperRef.current) {
                editorMaxHeight.current = wrapperRef.current.clientHeight
            }
        })

        return () => cancelAnimationFrame(calculateHeight)
    }, [showFullText, textValue])

    if (!isEditable && isEmpty) return null

    if (isEditingValue) {
        return (
            <DetailViewSubtitleAttributeEditor
                mt={mt}
                field={field}
                isLoading={isLoading}
                editorMaxHeight={editorMaxHeight.current}
                labelPlacement="hide"
                showFieldIcon={false}
                className={className}
            />
        )
    }

    return (
        <BaseAttribute
            mt={mt}
            field={field}
            isEmpty={isEmpty}
            isLoading={isLoading}
            cursor={isEditable ? 'text' : undefined}
            labelPlacement="hide"
            className={className}
            hideClearValueButton={true}
            showFieldIcon={false}
            center={false}
        >
            <Box ref={wrapperRef}>
                <LongTextValue
                    textValue={textValue}
                    maxLines={maxLines}
                    showFullText={showFullText}
                    setShowFullText={setShowFullText}
                    renderMoreLink={(renderOpts) => <MoreLink size={size} {...renderOpts} />}
                    style={{
                        lineHeight: '24px',
                    }}
                >
                    <DetailViewSubtitleAttributeContent
                        field={field}
                        value={value}
                        style={{
                            ...style,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                        }}
                        isLoading={isLoading}
                        size={effectiveSize}
                        weight={weight}
                        {...props}
                    />
                </LongTextValue>
            </Box>
        </BaseAttribute>
    )
}

type DetailViewSubtitleAttributeEditorProps = EditableAttributeProps & {
    editorMaxHeight?: number
    className?: string
    mt?: DetailViewSubtitleAttributeProps['mt']
}

const DetailViewSubtitleAttributeEditor: React.FC<DetailViewSubtitleAttributeEditorProps> = ({
    editorMaxHeight,
    field,
    isLoading,
    ...props
}) => {
    switch (field.field.type) {
        case 'string':
        case 'long_text':
            return (
                <LongTextAttributeEditor
                    field={field}
                    isLoading={isLoading}
                    editorMaxHeight={editorMaxHeight}
                    {...props}
                />
            )
        case 'document':
            return (
                <RichTextAttributeEditor
                    field={field}
                    isLoading={isLoading}
                    editorMaxHeight={editorMaxHeight}
                    {...props}
                />
            )
        default:
            return null
    }
}

type DetailViewSubtitleAttributeContentProps = {
    size?: React.ComponentProps<typeof Body>['size']
    weight?: React.ComponentProps<typeof Body>['weight']
    field: EditableAttribute
    value: unknown
    style?: React.CSSProperties
    className?: string
    isLoading?: boolean
}

const DetailViewSubtitleAttributeContent: React.FC<DetailViewSubtitleAttributeContentProps> =
    React.memo(function DetailViewSubtitleAttributeContent({ field, value, ...props }) {
        switch (field.field.type) {
            case 'string':
                return (
                    <TextAttributeDisplay field={field.field} value={value as string} {...props} />
                )

            case 'long_text':
                return (
                    <LongTextAttributeDisplay
                        field={field.field}
                        value={value as string}
                        {...props}
                    />
                )

            case 'document':
                return (
                    <RichTextAttributeDisplay
                        field={field.field}
                        value={value as RichTextValue}
                        {...props}
                    />
                )

            default:
                return null
        }
    })
